import {Stack} from '@mantine/core';
import React, {useCallback, useEffect, useLayoutEffect, useMemo, useState} from 'react';

import {assert} from 'Asserts';
import {MinimalStorableSummaryWithSchemaId} from 'constants/SampleConstants';
import {BioentitySummary} from 'graphql-client/declarative-translations/external/RESTTypes';
import {FetchedContainerInfo} from 'notebook/structured-tables/util/fetchContainersFromPlateWellPositions';
import {
  propagateMultipleItemColumnsToDestinationTable,
  StorableSummaryWithContainer,
} from 'processes/tables/column-mapping/propagateContentFromSourceToDestinationTable';
import {ProcessTaskStructuredTable} from 'processes/tables/ProcessTaskStructuredTableAdapter';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonStyle} from 'ui-library/buttons/Button';

import MappingRow, {MappingRowSummary} from './MappingRow';
import {useSelectTaskColumnMappingsModalStyles} from './selectTaskColumnMappingsModalStyles';
import {
  SelectTaskColumnMappingsModalCreationType,
  type ColumnMappingModalSubmitControls,
  type ColumnMappingRowState,
  type ColumnSummary,
  type ProcessTaskSchema,
  type SelectTargetColumnModalSubmitResult,
  type TaskColumnMappingInfo,
} from './selectTaskColumnMappingsModalTypes';

type SummariesById = Record<string, BioentitySummary | MinimalStorableSummaryWithSchemaId>;
type ColumnValues = Array<BioentitySummary> | Array<MinimalStorableSummaryWithSchemaId>;

function getDefaultFirstMappingRow(
  sourceColumns: Array<TaskColumnMappingInfo>,
  selectableBySource: Array<Array<ColumnSummary>>
): ColumnMappingRowState {
  const firstSelectableIndex = sourceColumns.findIndex((_col, i) => selectableBySource[i]?.length > 0);
  if (firstSelectableIndex === -1) {
    return {sourceColumnIndex: null, destinationColumnIndex: null};
  }
  return {sourceColumnIndex: firstSelectableIndex, destinationColumnIndex: 0};
}

/** Limit on the number of mapping rows that can be added. */
function getMaxMappingRowCount(
  sourceColumns: Array<TaskColumnMappingInfo>,
  selectableBySource: Array<Array<ColumnSummary>>
): number {
  const distinctDestinationCount = new Set(
    selectableBySource.flatMap((columnSummaries) =>
      columnSummaries.map((columnSummary) => columnSummary.destinationColumnIndex)
    )
  ).size;
  return Math.min(sourceColumns.length, Math.max(distinctDestinationCount, 1));
}

interface MultiColumnMappingModalContentProps {
  sourceColumns: Array<TaskColumnMappingInfo>;
  taskSchema: ProcessTaskSchema;
  taskTable: ProcessTaskStructuredTable;
  summariesById: SummariesById;
  fetchedContainerInfo: FetchedContainerInfo | null;
  creationType: SelectTaskColumnMappingsModalCreationType;
  selectableBySource: Array<Array<ColumnSummary>>;
  onRequestSubmit: (result: SelectTargetColumnModalSubmitResult) => void;
  getSourceColumnDisplayName: (column: TaskColumnMappingInfo) => string;
  isColumnDestinationWellForTransfer: (column: TaskColumnMappingInfo) => boolean;
  getSelectedSourceItems: (
    sourceColumn: TaskColumnMappingInfo,
    summariesById: SummariesById
  ) => {selectedSourceItems: ColumnValues; shouldFetchPlateInfo?: boolean};
  onSubmitControlsChange: (controls: ColumnMappingModalSubmitControls) => void;
  fieldText: string;
  multiTableIntroText: string;
}

export default function MultiColumnMappingModalContent({
  sourceColumns,
  taskSchema,
  taskTable,
  summariesById,
  fetchedContainerInfo,
  creationType,
  selectableBySource,
  onRequestSubmit,
  getSourceColumnDisplayName,
  isColumnDestinationWellForTransfer,
  getSelectedSourceItems,
  onSubmitControlsChange,
  fieldText,
  multiTableIntroText,
}: MultiColumnMappingModalContentProps): JSX.Element {
  const {classes} = useSelectTaskColumnMappingsModalStyles();
  const [mappingRows, setMappingRows] = useState<Array<ColumnMappingRowState>>(() => [
    getDefaultFirstMappingRow(sourceColumns, selectableBySource),
  ]);

  useEffect(() => {
    setMappingRows([getDefaultFirstMappingRow(sourceColumns, selectableBySource)]);
  }, [sourceColumns, selectableBySource]);

  const maxMappingRows = useMemo(
    () => getMaxMappingRowCount(sourceColumns, selectableBySource),
    [sourceColumns, selectableBySource]
  );

  /**
   * One entry per mapping row: resolves `mappingRows[i]` into the source column model, that source's
   * cell values, the matching task-field options (`selectableColumns` from `selectableBySource`), and
   * a clamped destination index for the picker. Used by submit, exclusivity (`usedSourceIndices` /
   * `usedDestinationNames`), and passed into `MappingRow` as `MappingRowSummary`.
   */
  const resolvedMappingRows = useMemo(
    () =>
      mappingRows.map((row) => {
        const {sourceColumnIndex, destinationColumnIndex} = row;
        const destUnset = destinationColumnIndex == null;
        if (sourceColumnIndex == null) {
          return {
            sourceColumn: null,
            selectedSourceItems: [] as ColumnValues,
            selectableColumns: [],
            destIdx: null,
            isSourceUnset: true,
            destUnset,
          };
        }
        const sourceColumn = sourceColumns[sourceColumnIndex];
        const {selectedSourceItems} = getSelectedSourceItems(sourceColumn, summariesById);
        const selectableColumns = selectableBySource[sourceColumnIndex] ?? [];
        const destIdx =
          selectableColumns.length === 0 || destinationColumnIndex == null
            ? null
            : Math.min(Math.max(0, destinationColumnIndex), selectableColumns.length - 1);
        return {
          sourceColumn,
          selectedSourceItems,
          selectableColumns,
          destIdx,
          isSourceUnset: false,
          destUnset,
        };
      }),
    [mappingRows, sourceColumns, summariesById, selectableBySource, getSelectedSourceItems]
  );

  const handleSubmit = useCallback((): void => {
    const columnMappings = resolvedMappingRows
      .filter(
        ({sourceColumn, selectableColumns, isSourceUnset, destUnset, destIdx}) =>
          sourceColumn != null &&
          !isSourceUnset &&
          !destUnset &&
          destIdx != null &&
          selectableColumns.length > 0
      )
      .map(({sourceColumn, selectedSourceItems, selectableColumns, destIdx}) => {
        assert(sourceColumn != null, 'Source column required for mapping');
        assert(destIdx != null, 'Destination index required for mapping');
        const itemsWithContainers = selectedSourceItems.map((item, rowNum) =>
          isColumnDestinationWellForTransfer(sourceColumn)
            ? {...item, container: fetchedContainerInfo?.containers?.[rowNum] ?? null}
            : item
        ) as Array<BioentitySummary> | Array<StorableSummaryWithContainer>;
        return {
          items: itemsWithContainers,
          destinationColumnIndex: selectableColumns[destIdx].destinationColumnIndex,
        };
      });
    assert(columnMappings.length > 0, 'At least one valid column mapping is required');
    onRequestSubmit({
      taskTable: propagateMultipleItemColumnsToDestinationTable({
        columnMappings,
        destinationTable: taskTable,
      }),
      shouldRunCreateModal: true,
    });
  }, [
    resolvedMappingRows,
    onRequestSubmit,
    taskTable,
    fetchedContainerInfo?.containers,
    isColumnDestinationWellForTransfer,
  ]);

  /**
   * Submit UX and warning banner: `validCount` is how many rows have a complete mapping (both a
   * source and destination selected, which enables Map / drives button label). `unmatchedDisplayNames`
   * lists sources that are picked but match no task fields (warning text). `unmatchedFromSelectedItemsOnly`
   * picks the "selected items" vs "source columns" wording when every such source is a `hasValues` column.
   */
  const mappingSubmitSummary = useMemo(() => {
    const unmatchedDisplayNames: Array<string> = [];
    let validCount = 0;
    let unmatchedHasValuesOnly = true;
    for (const {sourceColumn, selectableColumns, isSourceUnset, destUnset, destIdx} of resolvedMappingRows) {
      if (isSourceUnset || sourceColumn == null) {
        continue;
      }
      if (selectableColumns.length === 0) {
        unmatchedDisplayNames.push(getSourceColumnDisplayName(sourceColumn));
        if (!sourceColumn.hasValues) {
          unmatchedHasValuesOnly = false;
        }
        continue;
      }
      if (!destUnset && destIdx != null) {
        validCount += 1;
      }
    }
    const unmatchedFromSelectedItemsOnly = unmatchedDisplayNames.length > 0 && unmatchedHasValuesOnly;
    return {validCount, unmatchedDisplayNames, unmatchedFromSelectedItemsOnly};
  }, [resolvedMappingRows, getSourceColumnDisplayName]);

  const submitButtonText = useMemo(() => {
    const {validCount} = mappingSubmitSummary;
    const totalRows = mappingRows.length;
    if (validCount === 0) {
      return 'Map columns';
    }
    if (validCount === totalRows) {
      return totalRows === 1 ? 'Map column' : 'Map columns';
    }
    return `Map ${validCount} column${validCount === 1 ? '' : 's'}`;
  }, [mappingSubmitSummary, mappingRows.length]);

  // Submit is enabled when at least one mapping row has both a source and destination selected,
  // matching the partial-submission story advertised by `submitButtonText` (e.g. "Map 1 column").
  // Incomplete or unmatched rows are skipped at submit time; the warning banner still prompts the
  // user to fix unmatched sources or use "Continue without mapping".
  const mapSubmitDisabled = mappingSubmitSummary.validCount === 0;

  const canAddMoreMappingRows = useMemo(
    () => mappingRows.length < maxMappingRows,
    [mappingRows.length, maxMappingRows]
  );

  const {usedSourceIndices, usedDestinationNames} = useMemo(() => {
    const sourceIndices = new Set<number>();
    const destinationNames = new Set<string>();
    for (let i = 0; i < mappingRows.length; i++) {
      const row = mappingRows[i];
      if (row.sourceColumnIndex != null) {
        sourceIndices.add(row.sourceColumnIndex);
      }
      const resolvedRow = resolvedMappingRows[i];
      if (
        row.destinationColumnIndex != null &&
        resolvedRow != null &&
        resolvedRow.selectableColumns[row.destinationColumnIndex] != null
      ) {
        destinationNames.add(resolvedRow.selectableColumns[row.destinationColumnIndex].columnName);
      }
    }
    return {usedSourceIndices: sourceIndices, usedDestinationNames: destinationNames};
  }, [mappingRows, resolvedMappingRows]);

  useLayoutEffect(() => {
    onSubmitControlsChange({handleSubmit, submitButtonText, mapSubmitDisabled});
  }, [onSubmitControlsChange, handleSubmit, submitButtonText, mapSubmitDisabled]);

  return (
    <>
      <Stack spacing="xs" mb="sm">
        <span>{multiTableIntroText}</span>
      </Stack>
      <div className={classes.columnMappingsSection}>
        <div className={classes.columnMappingsTitle}>Column mappings</div>
        <div className={classes.mappingTableHeader}>
          <span className={classes.mappingHeaderCell}>Source column</span>
          <span className={classes.mappingHeaderCell}>
            {creationType === SelectTaskColumnMappingsModalCreationType.PROCESS ? 'Task field' : 'Field'}
          </span>
          {mappingRows.length > 1 ? <span className={classes.mappingHeaderRemove} /> : null}
        </div>
        {mappingRows.map((row, rowIdx) => {
          const mappingRowSummary: MappingRowSummary = resolvedMappingRows[rowIdx];
          return (
            <MappingRow
              key={rowIdx}
              sourceColumns={sourceColumns}
              rowSummary={mappingRowSummary}
              usedSourceIndices={usedSourceIndices}
              usedDestinationNames={usedDestinationNames}
              sourceColumnIndex={row.sourceColumnIndex}
              rowIndex={rowIdx}
              showRemove={mappingRows.length > 1}
              getSourceColumnDisplayName={getSourceColumnDisplayName}
              onSourceChange={(pickerIndex: number) => {
                setMappingRows((prev) => {
                  const next = [...prev];
                  next[rowIdx] = {
                    sourceColumnIndex: pickerIndex,
                    destinationColumnIndex: null,
                  };
                  return next;
                });
              }}
              onDestinationChange={(pickerIndex: number) => {
                setMappingRows((prev) => {
                  const next = [...prev];
                  next[rowIdx] = {...next[rowIdx], destinationColumnIndex: pickerIndex};
                  return next;
                });
              }}
              onRemove={() => {
                setMappingRows((prev) => prev.filter((_, i) => i !== rowIdx));
              }}
            />
          );
        })}
      </div>
      <Stack spacing={0} mt="sm" align="flex-start">
        <Button
          buttonStyle={ButtonStyle.LINK}
          text="+ Add column mapping"
          disabled={!canAddMoreMappingRows}
          onClick={() => {
            setMappingRows((prev) => [...prev, {sourceColumnIndex: null, destinationColumnIndex: null}]);
          }}
        />
      </Stack>
      {mappingSubmitSummary.unmatchedDisplayNames.length > 0 ? (
        <Stack spacing={0} mt="sm">
          <InlineBanner
            bannerType={InlineBannerType.WARNING}
            text={
              mappingSubmitSummary.unmatchedFromSelectedItemsOnly
                ? `The selected items don't match any ${fieldText}s in ${taskSchema.name}.`
                : `The source columns ${mappingSubmitSummary.unmatchedDisplayNames.join(
                    ', '
                  )} don't match any ${fieldText}s in ${taskSchema.name}.`
            }
            supportingInfo={
              mappingSubmitSummary.unmatchedFromSelectedItemsOnly
                ? 'You can still continue without mapping.'
                : 'Select a different source column, or continue without mapping.'
            }
          />
        </Stack>
      ) : null}
    </>
  );
}
